import { Fragment, lazy, useCallback, useEffect } from 'react'
import styles from './style.module.scss'
import { Button, Col, Container, Row } from 'react-bootstrap'
import Logo from 'src/walliro/assets/images/default/logo.png'
import LogoDark from 'src/walliro/assets/images/dark/logo.png'
import { useMain } from 'src/walliro/hook'
import { useSelector } from 'react-redux'
import {
  availableSelector,
  loadedSelector,
  networkSelector,
  providerNameSelector,
  shouldSwitchWalletChain,
  userAccountSelector,
  userEnsSelector,
} from 'src/logic/wallets/store/selectors'
import { ReturnValue, useStateHandler } from 'src/logic/hooks/useStateHandler'
import ProviderAccessible from './ProviderInfo/ProviderAccessible'
import ProviderDisconnected from './ProviderInfo/ProviderDisconnected'
import ConnectDetails from './ProviderDetails/ConnectDetails'
import onboard, { loadLastUsedProvider, removeLastUsedProvider } from 'src/logic/wallets/onboard'
import WalletSwitch from 'src/components/WalletSwitch'
import Notifications from './Notifications'
import Provider from './Provider'
import { UserDetails } from './ProviderDetails/UserDetails'
import { currentChainId } from 'src/logic/config/store/selectors'
import { isSupportedWallet } from 'src/logic/wallets/utils/walletList'
import { initPairing, isPairingSupported } from 'src/logic/wallets/pairing/utils'
import { ClickAwayListener, Grow, Popper } from '@material-ui/core'
import List from '@material-ui/core/List'
import classNames from 'classnames'
import { wrapInSuspense } from 'src/utils/wrapInSuspense'
import { getNetworkRootRoutes } from 'src/routes/routes'
import { getChainById } from 'src/config'
import { ChainId } from 'src/config/chain'
import { trackEvent } from 'src/utils/googleTagManager'
import { Link, useHistory } from 'react-router-dom'
import { OVERVIEW_EVENTS } from 'src/utils/events/overview'

const HidePairingModule = lazy(
  () => import('src/walliro/containers/AppLayout/Header/ProviderDetails/HidePairingModule'),
)
type IProps = {
  onToggleSafeList: () => void
  balance?: string
}

const WalletPopup = ({ anchorEl, providerDetails, open, onClose }) => {
  if (!open) {
    return <Fragment />
  }
  return (
    <Popper
      anchorEl={anchorEl}
      className={styles['dropdown']}
      open
      placement="bottom"
      popperOptions={{ positionFixed: true }}
    >
      <ClickAwayListener mouseEvent="onClick" onClickAway={onClose} touchEvent={false}>
        <List
          // className={classes.root}
          component="div"
        >
          {providerDetails}
        </List>
      </ClickAwayListener>
    </Popper>
  )
}

const HeaderComponent = ({ balance, onToggleSafeList }: IProps) => {
  const { theme } = useMain()
  const isDark = theme.color === 'dark'
  const provider = useSelector(providerNameSelector)
  const chainId = useSelector(currentChainId)
  const userAddress = useSelector(userAccountSelector)
  const ensName = useSelector(userEnsSelector)
  const loaded = useSelector(loadedSelector)
  const available = useSelector(availableSelector)

  useEffect(() => {
    const tryToConnectToLastUsedProvider = async () => {
      const lastUsedProvider = loadLastUsedProvider()
      const isProviderEnabled = lastUsedProvider && isSupportedWallet(lastUsedProvider)
      if (isProviderEnabled) {
        await onboard().walletSelect(lastUsedProvider)
      } else if (isPairingSupported()) {
        await initPairing()
      }
    }

    tryToConnectToLastUsedProvider()
  }, [chainId])

  const openDashboard = () => {
    const { wallet } = onboard().getState()
    return wallet.type === 'sdk' && wallet.dashboard
  }

  const onDisconnect = () => {
    onboard().walletReset()
    removeLastUsedProvider()
  }

  const getProviderInfoBased = () => {
    if (!loaded || !provider) {
      return <ProviderDisconnected />
    }

    return <ProviderAccessible connected={available} provider={provider} userAddress={userAddress} />
  }

  const getProviderDetailsBased = () => {
    if (!loaded) {
      return <ConnectDetails vertical />
    }

    return (
      <UserDetails
        connected={available}
        onDisconnect={onDisconnect}
        openDashboard={openDashboard()}
        provider={provider}
        userAddress={userAddress}
        ensName={ensName}
      />
    )
  }

  const info = getProviderInfoBased()
  const details = getProviderDetailsBased()

  const { clickAway: clickAwayNotifications, open: openNotifications, toggle: toggleNotifications } = useStateHandler()
  const { clickAway: clickAwayWallet, open: openWallet, toggle: toggleWallet } = useStateHandler()
  const { clickAway: clickAwayNetworks, open: openNetworks, toggle: toggleNetworks } = useStateHandler()

  const isWrongChain = useSelector(shouldSwitchWalletChain)

  return (
    <header className={styles['header']}>
      {isPairingSupported() && wrapInSuspense(<HidePairingModule />)}

      <Container fluid>
        <Row>
          <Col xs={12} lg>
            <div className={'text-center text-md-start d-flex align-items-center'}>
              <Link to={'/'}>
                <img src={isDark ? Logo : LogoDark} alt="walliro" />
              </Link>
              <Button
                variant="shadow"
                className={'btn-icon fixed btn-circle ms-auto ms-lg-4'}
                onClick={onToggleSafeList}
              >
                <i className="material-icons">east</i>
              </Button>
            </div>
          </Col>
          {isWrongChain && (
            <Col xs={12} lg={'auto'} className="my-auto">
              <WalletSwitch />
            </Col>
          )}

          <Col xs={12} lg={'auto'} className="mt-3 mt-lg-0">
            <div className="d-flex flex-wrap align-items-center">
              {available && balance && (
                <div
                  className={classNames('color-txt fz-18', styles['balance-text'])}
                  dangerouslySetInnerHTML={{ __html: balance }}
                />
              )}
              <div className="ms-4">
                <Notifications
                  open={openNotifications}
                  toggle={toggleNotifications}
                  clickAway={clickAwayNotifications}
                />
              </div>
              <Button
                variant="shadow"
                className="ms-2 btn-icon fixed btn-circle"
                onClick={() => theme.changeColor(isDark ? 'default' : 'dark')}
              >
                <i className="material-icons">{isDark ? 'light_mode' : 'dark_mode'}</i>
              </Button>
              <div className="ms-4">
                <Provider
                  info={info}
                  open={openWallet}
                  toggle={toggleWallet}
                  render={(providerRef) =>
                    providerRef.current && (
                      <WalletPopup
                        anchorEl={providerRef.current}
                        providerDetails={details}
                        open={openWallet}
                        onClose={clickAwayWallet}
                      />
                    )
                  }
                />
              </div>
            </div>
          </Col>
        </Row>
      </Container>
    </header>
  )
}

export default HeaderComponent
